/**
 * The animated logo: one strip of film draws a WA ligature (the W's last climb
 * is also the A's left leg, and the A's bar comes back up from its right
 * foot), turns white and swells to the letters' weight - and that solid
 * ligature is the mark (candidate B, the storyboard's 1.66 s frame). It holds
 * there while WATCH ANYTHING sets itself underneath.
 *
 * Every shape is Syncopate Bold's own outline in font units, so nothing waits
 * on a web font and the hand-off from film to letters lands exactly. The
 * caller owns the <svg> and its viewBox; this fills it with a 1280x720 scene.
 * Drawn by hand every frame rather than in CSS, because the split moves the
 * corners of each stroke, which CSS cannot animate.
 *
 * public/landing/wa-logo.js is a copy of this file for the static landing
 * page, which sits outside the React build. Change both together.
 */

const NS = 'http://www.w3.org/2000/svg';

// Font units: 2048/em, cap height 1374, y up.
const S = 0.16012; // the big WA: cap height 220
const BASE = 470;
const CAP = BASE - 1374 * S;
const XL = 366.2; // the ligature's origin (the W's)
const XAL = XL + 1537.5 * S; // the A inside the ligature: its apex is the W's last top
const SF = 0.0335; // the title under the mark
const TRACK = 164;
const EXT = 22; // how far each run of film carries past its fold

const TITLE = 'WATCH ANYTHING';
const ADV = { W: 2687, A: 1872, T: 1599, C: 1860, H: 2116, ' ': 770, N: 2130, Y: 1784, I: 801, G: 2011 };
const GLYPH = {
  W: 'M1550 1374 1901 420 2281 1374H2666L2114 0H1702L1343 924L985 0H573L20 1374H406L778 440L1137 1374Z',
  A: 'M1319 260H553L424 0H10L729 1374H1143L1862 0H1448ZM686 528H1188L938 1034Z',
  T: 'M985 1075V0H614V1075H31V1374H1569V1075Z',
  C: 'M1729 94Q1657 64 1584 40Q1511 16 1433 -2Q1355 -19 1268 -28Q1182 -37 1083 -37Q873 -37 696 8Q520 53 392 144Q265 234 194 370Q123 505 123 686Q123 867 194 1002Q265 1138 392 1228Q520 1319 696 1364Q873 1409 1083 1409Q1182 1409 1268 1400Q1355 1391 1433 1374Q1511 1356 1584 1332Q1657 1308 1729 1278V944Q1672 975 1609 1006Q1546 1036 1470 1060Q1394 1085 1303 1100Q1212 1116 1098 1116Q923 1116 808 1080Q692 1044 623 984Q554 924 526 846Q498 769 498 686Q498 631 510 578Q522 524 550 476Q578 429 623 389Q668 349 735 320Q802 291 892 274Q981 258 1098 258Q1212 258 1303 272Q1394 287 1470 311Q1546 335 1609 366Q1672 396 1729 428V94Z',
  H: 'M1530 0V573H586V0H215V1374H586V874H1530V1374H1901V0Z',
  N: 'M1513 0 586 944V0H215V1374H618L1546 426V1374H1915V0Z',
  Y: 'M1077 561V0H707V561L41 1374H457L893 821L1327 1374H1743Z',
  I: 'M215 0V1374H586V0Z',
  G: 'M1100 1409Q1302 1409 1476 1374Q1649 1340 1794 1286V950Q1737 982 1666 1012Q1594 1042 1510 1065Q1426 1088 1330 1102Q1234 1116 1128 1116Q996 1116 898 1097Q800 1078 730 1046Q660 1014 615 972Q570 929 544 881Q518 833 508 783Q498 733 498 686Q498 645 506 597Q515 549 538 501Q562 453 604 409Q646 365 714 331Q781 297 876 276Q971 256 1102 256Q1168 256 1217 260Q1266 264 1306 271Q1347 278 1382 288Q1418 298 1456 309V475H1040V776H1827V74Q1753 49 1662 28Q1570 8 1472 -6Q1375 -21 1277 -29Q1179 -37 1092 -37Q957 -37 833 -19Q709 -1 602 36Q494 74 406 132Q317 190 254 270Q192 350 158 454Q123 557 123 686Q123 814 159 918Q195 1022 260 1102Q324 1182 414 1240Q505 1298 614 1336Q722 1373 846 1391Q969 1409 1100 1409Z'
};

const pt = (ox, x, y) => [ox + x * S, BASE - y * S];
const cl = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const io = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const out = (x) => 1 - Math.pow(1 - x, 3);
const sine = (x) => -(Math.cos(Math.PI * x) - 1) / 2;
const mix = (a, b, k) => a + (b - a) * k;

// The strip: down, up, down, up the W, down the A's right leg, then back up-left
// from its foot as the bar. Alternate runs show the film's back face.
const SEGS = [
  { a: pt(XL, 213, 1374), b: pt(XL, 779, 0), w: 369 * S, back: false },
  { a: pt(XL, 779, 0), b: pt(XL, 1343.5, 1374), w: 369 * S, back: true },
  { a: pt(XL, 1343.5, 1374), b: pt(XL, 1908, 0), w: 369 * S, back: false },
  { a: pt(XL, 1908, 0), b: pt(XL, 2473.5, 1374), w: 369 * S, back: true },
  { a: pt(XAL, 936, 1374), b: pt(XAL, 1655, 0), w: 367 * S, back: false },
  { a: pt(XAL, 1620, 0), b: pt(XAL, 600.5, 560), w: 236.6 * S, back: true }
];
let TOTAL = 0;
SEGS.forEach((s) => {
  const dx = s.b[0] - s.a[0];
  const dy = s.b[1] - s.a[1];
  const len = Math.hypot(dx, dy);
  const ux = dx / len;
  const uy = dy / len;
  s.A = [s.a[0] - ux * EXT, s.a[1] - uy * EXT];
  s.B = [s.b[0] + ux * EXT, s.b[1] + uy * EXT];
  s.L = len + 2 * EXT;
  s.c = TOTAL;
  TOTAL += s.L;
});

/**
 * The mark: the solid ligature, as ONE outline. The strokes above overlap
 * where they meet, and drawn as separate overlapping shapes they are at the
 * mercy of the fill rule - wound alternately, every joint punched a hole
 * (the first B that shipped); wound alike, any renderer filling evenodd
 * punches them again. This is their union, computed once: an 18-corner
 * outline wound clockwise, and the A's counter as a hole wound the other
 * way, so it fills the same under nonzero and evenodd and has no internal
 * edges to leave anti-aliasing seams. Font units, cap height 1374, y down;
 * x 0 is the W's left foot at font x 20.
 *
 * It matches the storyboard's own 1.66 s frame pixel for pixel. This is the
 * one definition: components/Logo.js imports it, the landing copy of this
 * file carries it, and scripts/make-icons.js reads it for every icon and
 * checks the landing header's inline copy against it.
 */
export const MARK =
  'M0 0 386 0 756.3 878.73 1117 0 1530 0 1890.7 878.73 2254.31 15.87 2246 0 2660 0 3378.79 1371.68 3383 1374 2892 1374 2238.18 1015.12 2094 1374 1682 1374 1323.5 502.18 965 1374 553 1374ZM2472.42 432.08 2327.17 793.61 2797.28 1052.03Z';
export const MARK_WIDTH = 3383;
export const MARK_HEIGHT = 1374;

// Where the mark sits in the scene: on the film it replaces, slid so the
// whole lockup is centred
const MARK_X = XL + 20 * S;
const MARK_DX = 640 - (MARK_X + (MARK_WIDTH * S) / 2);

// The title sets under the mark, centred on it
const TITLE_BASE = BASE + 38 + 1374 * SF;
const PLACE = [];
(() => {
  let width = 0;
  for (let i = 0; i < TITLE.length; i++) width += ADV[TITLE[i]] + (i < TITLE.length - 1 ? TRACK : 0);
  const x = 640 - (width * SF) / 2;
  let cum = 0;
  for (let i = 0; i < TITLE.length; i++) {
    PLACE[i] = x + cum * SF;
    cum += ADV[TITLE[i]] + TRACK;
  }
})();

/** Seconds until the lockup has settled (and the address, when shown). */
export const LOGO_SETTLED = 2.7;
export const LOGO_SETTLED_WITH_URL = 3.0;

let uid = 0;

const make = (tag, attrs, parent) => {
  const el = document.createElementNS(NS, tag);
  Object.keys(attrs).forEach((key) => el.setAttribute(key, attrs[key]));
  if (parent) parent.appendChild(el);
  return el;
};

/**
 * Fill `svg` with the scene and return controls: play() runs it once from the
 * start, finish() jumps to the settled lockup, seek(t) holds one moment,
 * cancel() stops drawing.
 * `url: true` sets the address under the title.
 *
 * Settled, the mark spans y 250-470 of the 1280x720 scene, centred on x 640,
 * the title's caps 508-554 under it, and the address (when shown) sits on
 * 598. Callers frame that with their viewBox. Nothing moves after 1.65 s but
 * the title and address arriving: the mark that holds is the solid state, not
 * a frame caught mid-split.
 */
export const createWaLogo = (svg, { url = false } = {}) => {
  const id = `wa-band-${++uid}`;
  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const defs = make('defs', {}, svg);
  const clip = make('clipPath', { id, clipPathUnits: 'userSpaceOnUse' }, defs);
  const band = make('rect', { x: 0, y: 0, width: 1280, height: 720 }, clip);

  const scene = make('g', { transform: `translate(${MARK_DX} 0)` }, svg);
  const film = make('g', { 'clip-path': `url(#${id})`, fill: 'none', 'stroke-linecap': 'butt' }, scene);
  const segs = SEGS.map(() => {
    const g = make('g', {}, film);
    return {
      under: make('path', { stroke: '#000', d: '' }, g),
      face: make('path', { d: '' }, g),
      holes: make('path', { stroke: '#000', 'stroke-dasharray': '6 8', d: '' }, g),
      cover: make('path', { d: '' }, g)
    };
  });

  // The finished mark takes over from the film the moment it is solid
  const mark = make('path', {
    fill: '#fff',
    d: MARK,
    opacity: 0,
    transform: `translate(${MARK_X} ${CAP}) scale(${S})`
  }, scene);

  const lettersG = make('g', { fill: '#fff' }, svg);
  const letters = [];
  let order = 0;
  for (let i = 0; i < TITLE.length; i++) {
    if (TITLE[i] === ' ') continue;
    const g = make('g', { opacity: 0 }, lettersG);
    make('path', { d: GLYPH[TITLE[i]], 'fill-rule': 'evenodd' }, g);
    letters.push({ i, g, order: order++ });
  }

  const address = url
    ? make('text', {
        x: 640,
        y: TITLE_BASE + 44,
        'text-anchor': 'middle',
        fill: '#8c8c8c',
        opacity: 0,
        'font-family': "'Geist Mono', ui-monospace, monospace",
        'font-size': 12,
        'font-weight': 500,
        'letter-spacing': '4.8'
      }, svg)
    : null;
  if (address) address.textContent = 'watchanything.stream';

  const frame = (t) => {
    // 1 · the strip lays itself down, one run after another
    const u = sine(cl((t - 0.1) / 1.25)) * TOTAL;
    // 2 · it closes its holes, turns white and swells to the letter's weight;
    //     the band trims the folds that carried past cap height and baseline
    const wv = io(cl((t - 1.38) / 0.27));
    const filmOn = t < 1.65;
    if (t < 1.38) {
      band.setAttribute('y', 0);
      band.setAttribute('height', 720);
    } else {
      const y0 = mix(CAP - 30, CAP, wv);
      const y1 = mix(BASE + 30, BASE, wv);
      band.setAttribute('y', y0);
      band.setAttribute('height', y1 - y0);
    }
    film.setAttribute('opacity', filmOn ? 1 : 0);
    if (filmOn) {
      SEGS.forEach((s, i) => {
        const el = segs[i];
        const p = cl((u - s.c) / s.L);
        const d = p <= 0 ? '' : `M${s.A[0]} ${s.A[1]}L${mix(s.A[0], s.B[0], p)} ${mix(s.A[1], s.B[1], p)}`;
        const w = mix(44, s.w, wv);
        const grey = Math.round(mix(s.back ? 169 : 245, 255, wv));
        const col = `rgb(${grey},${grey},${grey})`;
        el.under.setAttribute('d', d);
        el.under.setAttribute('stroke-width', w + 4);
        el.under.setAttribute('opacity', 1 - wv);
        el.face.setAttribute('d', d);
        el.face.setAttribute('stroke-width', w);
        el.face.setAttribute('stroke', col);
        el.holes.setAttribute('d', d);
        el.holes.setAttribute('stroke-width', w * 0.77);
        el.holes.setAttribute('opacity', 1 - wv);
        // The perforations travel while the film is moving, then stop
        el.holes.setAttribute('stroke-dashoffset', -Math.min(t, 1.38) * 80);
        el.cover.setAttribute('d', d);
        el.cover.setAttribute('stroke-width', w * 0.5);
        el.cover.setAttribute('stroke', col);
        el.cover.setAttribute('opacity', 1 - wv);
      });
    }

    // 3 · solid, it is the mark: hand over to the one-outline path and hold
    mark.setAttribute('opacity', filmOn ? 0 : 1);

    // 4 · WATCH ANYTHING sets itself underneath, letter by letter
    letters.forEach(({ i, g, order: n }) => {
      const q = out(cl((t - 1.85 - n * 0.03) / 0.45));
      g.setAttribute('transform', `translate(${PLACE[i] - (1 - q) * 14} ${TITLE_BASE}) scale(${SF} ${-SF})`);
      g.setAttribute('opacity', q);
    });

    if (address) {
      const q = out(cl((t - 2.5) / 0.5));
      address.setAttribute('opacity', q);
      address.setAttribute('transform', `translate(0 ${(1 - q) * 6})`);
    }
  };

  let raf = 0;
  const end = url ? LOGO_SETTLED_WITH_URL : LOGO_SETTLED;

  return {
    play() {
      cancelAnimationFrame(raf);
      const t0 = performance.now();
      const tick = (now) => {
        const t = (now - t0) / 1000;
        frame(Math.min(t, end));
        if (t < end) raf = requestAnimationFrame(tick);
      };
      frame(0);
      raf = requestAnimationFrame(tick);
    },
    finish() {
      cancelAnimationFrame(raf);
      frame(end);
    },
    /** Draw one moment, `t` seconds in, and stay there. */
    seek(t) {
      cancelAnimationFrame(raf);
      frame(t);
    },
    cancel() {
      cancelAnimationFrame(raf);
    }
  };
};
